import React, { useState, useEffect, useCallback } from 'react';
import { FiChevronRight } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { typography } from '../styles/typography';
import '../styles/responsive-table.css';
import axios from '../config/api';
import { useAuth } from '@clerk/clerk-react';
import { formatTime, formatTimeDifference, getDeltaBestLapColor, getDeltaOptimalColor } from '../utils/timeUtils';
import { useAnalysisCache } from '../context/AnalysisCacheContext';
import OptimalTimePopup from './OptimalTimePopup';

interface AnalysisResultsProps {
  sessionId: string;
  onNewUpload: () => void;
  onStatusChange?: () => void;
}

interface SessionInfo {
  id: string;
  name: string;
  created_at: string;
  has_analysis: boolean;
  latest_job_id: string | null;
  latest_job_status: string | null;
  lap_times: number[] | null;
  best_lap_time: number | null;
  lap_count: number | null;
}

interface AnalysisData {
  id: string;
  status: string;
  session_result?: any;
  [key: string]: any;
}

const AnalysisResults: React.FC<AnalysisResultsProps> = ({ sessionId, onNewUpload, onStatusChange }) => {
  const navigate = useNavigate();
  const [sessionInfo, setSessionInfo] = useState<SessionInfo | null>(null);
  const [analysis, setAnalysis] = useState<AnalysisData | null>(null);
  const [isRunningAnalysis, setIsRunningAnalysis] = useState(false);
  const [runAnalysisError, setRunAnalysisError] = useState<string | null>(null);
  const intervalRef = React.useRef<NodeJS.Timeout | null>(null);
  const isActiveRef = React.useRef(true);
  const { getToken } = useAuth();
  const { getCachedAnalysis, setCachedAnalysis } = useAnalysisCache();
  const [showOptimalTimePopup, setShowOptimalTimePopup] = useState(false);

  // Centralized color constants
  const colors = {
    primary: '#6366f1',
    textPrimary: '#f3f4f6',
    textSecondary: '#9ca3af',
    textMuted: '#9ca3af',
    danger: '#f87171'
  };

  const hoverStyles = {
    default: 'var(--surface)',
    hover: 'var(--surface-hover)',
    transition: 'background-color 0.2s'
  };

  const lapTimeStyles = {
    label: {
      color: colors.textMuted,
      fontSize: typography.xs,
      marginBottom: '0.125rem',
      lineHeight: '1'
    },
    value: {
      fontSize: typography.dataLarge,
      fontWeight: 'bold',
      lineHeight: '1'
    }
  };

  const thStyle: React.CSSProperties = {
    textAlign: 'center',
    fontWeight: 'bold',
    color: 'white',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    padding: '1.5rem 1rem',
    fontSize: typography.tableHeader
  };

  const hasLapGpsData = (lap: any) =>
    lap.position?.latitude && lap.position?.longitude &&
    lap.position.latitude.length > 0 && lap.position.longitude.length > 0;

  const getAuthToken = useCallback(async () => {
    try {
      return await getToken();
    } catch (error) {
      console.error('Failed to get access token:', error);
      throw error;
    }
  }, [getToken]);

  // Use a ref so onStatusChange doesn't destabilize fetchSession's useCallback
  const onStatusChangeRef = React.useRef(onStatusChange);
  useEffect(() => {
    onStatusChangeRef.current = onStatusChange;
  }, [onStatusChange]);

  // Use a ref so fetchSession doesn't destabilize the polling useEffect
  const fetchSessionRef = React.useRef<() => Promise<void>>(async () => {});

  const handleShowPathVisualization = React.useCallback((lap: any, analyzed: boolean) => {
    navigate(`/dashboard/session/${sessionId}/lap/${lap.lap_number}`, { state: { analyzed } });
  }, [navigate, sessionId]);

  const sessionResult = React.useMemo(() => analysis?.session_result || analysis?.result || analysis, [analysis]);
  const analyzedLaps = React.useMemo(() => sessionResult?.lap_results || [], [sessionResult]);

  // Merge all session laps with analysis results so non-analyzed laps still appear
  const laps = React.useMemo(() => {
    const lapTimes = sessionInfo?.lap_times || [];
    if (lapTimes.length === 0) return analyzedLaps;

    // Index analyzed laps by lap_number for quick lookup
    const analyzedByNumber = new Map<number, any>();
    for (const lap of analyzedLaps) {
      analyzedByNumber.set(lap.lap_number, lap);
    }

    return lapTimes.map((lapTime: number, index: number) => {
      const lapNumber = index + 1;
      const analyzed = analyzedByNumber.get(lapNumber);
      if (analyzed) return analyzed;

      // Non-analyzed lap: build a minimal structure matching the analyzed shape
      return {
        lap_number: lapNumber,
        actual: { lap_time: lapTime },
        optimal: null,
        position: { latitude: [], longitude: [], displacement: [] },
        convenience: null,
        analysis: null,
      };
    });
  }, [analyzedLaps, sessionInfo]);

  const sessionLapTimes = React.useMemo(() => sessionInfo?.lap_times || [], [sessionInfo]);
  const getSessionLapTime = useCallback(
    (lap: any) => sessionLapTimes[lap.lap_number - 1] ?? lap.actual.lap_time,
    [sessionLapTimes]
  );

  const fastestLap = React.useMemo(() => {
    if (laps.length === 0) return null;
    return laps.reduce((fastest: any, lap: any) =>
      getSessionLapTime(lap) < getSessionLapTime(fastest) ? lap : fastest
    );
  }, [laps, getSessionLapTime]);

  const fastestOptimalLap = React.useMemo(() => {
    const analyzed = laps.filter((lap: any) => lap.optimal);
    if (analyzed.length === 0) return null;
    return analyzed.reduce((fastest: any, lap: any) =>
      lap.optimal.lap_time < fastest.optimal.lap_time ? lap : fastest
    );
  }, [laps]);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const fetchAnalysisData = useCallback(async (jobId: string): Promise<boolean> => {
    const cached = getCachedAnalysis(jobId);
    if (cached?.status === 'completed') {
      setAnalysis(cached);
      return true;
    }
    try {
      const token = await getAuthToken();
      const response = await axios.get(`/api/analysis/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data;
      if (data.status === 'completed') {
        setCachedAnalysis(jobId, data);
      }
      if (isActiveRef.current) {
        setAnalysis(data);
      }
      return true;
    } catch (err) {
      console.error('Failed to fetch analysis data:', err);
      return false;
    }
  }, [getCachedAnalysis, setCachedAnalysis, getAuthToken]);

  const fetchSession = useCallback(async () => {
    if (!isActiveRef.current) return;
    try {
      const token = await getAuthToken();
      const response = await axios.get(`/api/sessions/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!isActiveRef.current) return;

      const session: SessionInfo = response.data;

      setSessionInfo(prev => {
        // Detect completion/failure to notify sidebar
        if (prev?.latest_job_status !== session.latest_job_status &&
            (session.latest_job_status === 'completed' || session.latest_job_status === 'failed')) {
          setTimeout(() => onStatusChangeRef.current?.(), 0);
        }
        return session;
      });

      if (session.latest_job_status === 'completed' && session.latest_job_id) {
        // Only stop polling once we've actually loaded the analysis payload.
        // If the analysis fetch races the job-row write and 4xx/5xx's, keep polling
        // so the next tick retries instead of leaving the UI stuck blank.
        const ok = await fetchAnalysisData(session.latest_job_id);
        if (ok) stopPolling();
      } else if (session.latest_job_status === 'failed' || !session.has_analysis) {
        stopPolling();
      }
      // else pending/processing: keep polling
    } catch (err) {
      console.error('Failed to fetch session:', err);
    }
  // onStatusChange intentionally omitted — accessed via ref to prevent re-render loops
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, getAuthToken, fetchAnalysisData, stopPolling]);

  // Keep ref in sync so the polling interval always calls the latest fetchSession
  useEffect(() => {
    fetchSessionRef.current = fetchSession;
  });

  const handleRunAnalysis = useCallback(async () => {
    setIsRunningAnalysis(true);
    setRunAnalysisError(null);
    try {
      const token = await getAuthToken();
      await axios.post(`/api/sessions/${sessionId}/analyze`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsRunningAnalysis(false);
      setSessionInfo(prev => prev ? { ...prev, has_analysis: true, latest_job_status: 'pending' } : prev);
      // Start polling if not already running
      if (!intervalRef.current) {
        intervalRef.current = setInterval(fetchSession, 3000);
      }
      fetchSession();
    } catch (err: any) {
      console.error('Failed to start analysis:', err);
      setIsRunningAnalysis(false);
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || err?.response?.data?.message;
      if (status === 402) {
        setRunAnalysisError(detail || "You're out of analysis credits. Visit the pricing page to add more.");
      } else {
        setRunAnalysisError(detail || 'Failed to start analysis. Please try again.');
      }
    }
  }, [getAuthToken, sessionId, fetchSession]);

  useEffect(() => {
    isActiveRef.current = true;
    setAnalysis(null);
    setSessionInfo(null);

    fetchSessionRef.current();
    intervalRef.current = setInterval(() => fetchSessionRef.current(), 3000);

    return () => {
      isActiveRef.current = false;
      stopPolling();
    };
  // fetchSession intentionally omitted — accessed via ref to prevent re-render loops
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, stopPolling]);

  // Loading state
  if (!sessionInfo) {
    return null;
  }

  const isAnalysisInProgress =
    isRunningAnalysis ||
    sessionInfo.latest_job_status === 'pending' ||
    sessionInfo.latest_job_status === 'processing';
  const isAnalysisFailed = sessionInfo.latest_job_status === 'failed';

  // No loaded analysis data — show pre-analysis view (idle / processing / failed).
  // When a rerun is in progress, `analysis` still holds the previous results, so
  // we fall through to the completed view with an in-progress pill.
  if (!analysis) {
    // Transient: session says completed but fetchAnalysisData hasn't returned yet,
    // or the analysis fetch failed and we're retrying. Show a small loader instead
    // of a blank page so the user has feedback.
    if (sessionInfo.has_analysis && !isAnalysisInProgress && !isAnalysisFailed) {
      return (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh',
          width: '100%',
          gap: '0.625rem',
          color: '#9ca3af',
          fontSize: '0.9rem',
        }}>
          <div
            style={{
              width: '14px',
              height: '14px',
              border: '2px solid rgba(99, 102, 241, 0.3)',
              borderTopColor: '#6366f1',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }}
          />
          <span>Loading analysis results…</span>
        </div>
      );
    }
    const lapTimes = sessionInfo.lap_times || [];
    const bestLapTime = sessionInfo.best_lap_time;

    if (lapTimes.length === 0) {
      return (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          width: '100%',
          padding: '2rem'
        }}>
          <h2 style={{ fontSize: '1.5rem', color: '#f3f4f6', textAlign: 'center' }}>
            There are no complete laps in this session.
          </h2>
        </div>
      );
    }

    return (
      <div style={{ padding: '1rem', width: '100%', maxWidth: '1400px', margin: '0 auto' }}>
        <h2 style={{
          fontSize: '2rem',
          fontWeight: 'bold',
          color: '#f3f4f6',
          marginBottom: '1.5rem',
          marginLeft: '1.5rem',
          marginTop: '0.5rem',
          lineHeight: '40px',
          display: 'flex',
          alignItems: 'center',
          height: '40px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>
          {sessionInfo.name}
        </h2>

        {/* Fastest Lap + Run Analysis side by side */}
        <div style={{ marginBottom: '3rem' }}>
          <h3 style={{
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: '#f3f4f6',
            marginBottom: '1.5rem',
            marginLeft: '1.5rem'
          }}>
            Fastest Laps
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '1.5rem',
            maxWidth: '1000px',
            margin: '0 auto',
            padding: '0 1.5rem'
          }}
          className="fastest-laps-grid">
            {/* Fastest Lap Card */}
            <div style={{
              backgroundColor: hoverStyles.default,
              borderRadius: '1rem',
              border: '1px solid var(--surface-selected)',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              padding: '1.5rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <div>
                  <h4 style={{ color: '#f3f4f6', fontSize: typography.h4, fontWeight: 'bold', margin: 0 }}>Fastest Lap</h4>
                  <p style={{ color: colors.textSecondary, fontSize: '0.875rem', margin: 0, marginTop: '0.125rem', marginBottom: '0.75rem' }}>Best actual lap time</p>
                </div>
              </div>
              <div>
                <div style={lapTimeStyles.label}>Lap Time</div>
                <div style={{ ...lapTimeStyles.value, color: '#f3f4f6' }}>
                  {bestLapTime ? formatTime(bestLapTime) : '—'}
                </div>
              </div>
            </div>

            {/* Run Analysis Card */}
            <div style={{
              backgroundColor: hoverStyles.default,
              borderRadius: '1rem',
              border: `2px solid ${colors.primary}`,
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              padding: '2rem',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              gap: '1rem'
            }}>
              <div>
                <h4 style={{ color: colors.textPrimary, fontSize: typography.h4, fontWeight: 'bold', margin: 0 }}>Optimal Lap Analysis</h4>
                <p style={{ color: colors.textSecondary, fontSize: '0.875rem', margin: 0, marginTop: '0.5rem' }}>
                  {isAnalysisInProgress
                    ? 'This may take up to 1 minute'
                    : isAnalysisFailed
                      ? 'There was an error processing your track data'
                      : 'Run analysis to see optimal lap times and where time is lost'}
                </p>
              </div>
              {isAnalysisInProgress ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', color: colors.textPrimary, fontSize: '0.9rem', fontWeight: 600 }}>
                  <div
                    style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid rgba(99, 102, 241, 0.3)',
                      borderTopColor: colors.primary,
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                    }}
                  />
                  <span>Analyzing…</span>
                </div>
              ) : (
                <button
                  onClick={handleRunAnalysis}
                  disabled={isRunningAnalysis}
                  style={{
                    backgroundColor: colors.primary,
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.5rem',
                    padding: '0.625rem 1.5rem',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    cursor: isRunningAnalysis ? 'not-allowed' : 'pointer',
                    opacity: isRunningAnalysis ? 0.7 : 1
                  }}
                >
                  {isAnalysisFailed ? 'Retry Analysis' : 'Run Analysis'}
                </button>
              )}
              {runAnalysisError && !isAnalysisInProgress && (
                <div style={{
                  color: colors.danger,
                  fontSize: '0.8125rem',
                  marginTop: '0.25rem',
                  textAlign: 'center',
                }}>
                  {runAnalysisError}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Basic lap times table */}
        {lapTimes.length > 0 && (
          <div>
            <h3 style={{
              fontSize: '1.5rem',
              fontWeight: 'bold',
              color: '#f3f4f6',
              marginBottom: '1.5rem',
              marginLeft: '1.5rem',
              display: 'flex',
              alignItems: 'center'
            }}>
              All Laps ({lapTimes.length})
            </h3>
            <div style={{
              backgroundColor: hoverStyles.default,
              borderRadius: '1rem',
              border: '1px solid var(--surface-selected)',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              width: 'fit-content',
              maxWidth: '100%',
              overflowX: 'auto',
              margin: '0 auto'
            }}>
              <table className="responsive-table">
                <thead style={{ backgroundColor: 'var(--surface-hover)', borderBottom: '3px solid #6366f1' }}>
                  <tr>
                    <th style={{ ...thStyle, backgroundColor: 'rgba(75, 85, 99, 0.15)' }}>Lap</th>
                    <th style={thStyle}>Lap Time</th>
                    <th style={thStyle}>Delta Best Lap</th>
                  </tr>
                </thead>
                <tbody>
                  {lapTimes.map((lapTime: number, index: number) => {
                    const isFastest = bestLapTime !== null && lapTime === bestLapTime;
                    const deltaBest = bestLapTime !== null ? lapTime - bestLapTime : 0;
                    return (
                      <tr key={index}
                        onClick={() => navigate(`/dashboard/session/${sessionId}/lap/${index + 1}`)}
                        style={{
                          backgroundColor: isFastest ? 'rgba(16, 185, 129, 0.1)' : hoverStyles.default,
                          borderBottom: '1px solid #374151',
                          cursor: 'pointer'
                        }}>
                        <td style={{
                          padding: '0.625rem 1rem', textAlign: 'center',
                          backgroundColor: isFastest ? 'rgba(16, 185, 129, 0.15)' : 'var(--surface-light)'
                        }}>
                          <span style={{ color: '#f3f4f6', fontWeight: 'bold', fontSize: typography.dataLarge }}>
                            {index + 1}
                          </span>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <div style={{ fontWeight: 'bold', fontSize: typography.dataLarge, color: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                            {formatTime(lapTime)}
                            <FiChevronRight style={{ color: '#6b7280' }} />
                          </div>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <div style={{
                            fontWeight: 'bold', fontSize: typography.dataLarge,
                            color: getDeltaBestLapColor(deltaBest)
                          }}>
                            {formatTimeDifference(deltaBest)}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{
      padding: '1rem',
      width: '100%',
      maxWidth: '1400px',
      margin: '0 auto'
    }}>
      <h2 style={{
        fontSize: '2rem',
        fontWeight: 'bold',
        color: '#f3f4f6',
        marginBottom: '1.5rem',
        marginLeft: '1.5rem',
        marginTop: '0.5rem',
        lineHeight: '40px',
        display: 'flex',
        alignItems: 'center',
        height: '40px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      }}>
        Session Analysis - {sessionInfo.name || analysis?.name || 'Loading...'}
      </h2>

      {sessionInfo.created_at && (
        <div style={{
          fontSize: '0.875rem',
          color: colors.textSecondary,
          marginLeft: '1.5rem',
          marginTop: '-0.75rem',
          marginBottom: '1.5rem',
        }}>
          {new Date(sessionInfo.created_at).toLocaleDateString()}
        </div>
      )}

      {isAnalysisInProgress && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          width: 'fit-content',
          marginLeft: '1.5rem',
          marginBottom: '1.5rem',
          padding: '0.375rem 0.75rem',
          borderRadius: '9999px',
          backgroundColor: 'rgba(99, 102, 241, 0.12)',
          border: '1px solid rgba(99, 102, 241, 0.4)',
          color: '#c7d2fe',
          fontSize: '0.8125rem',
          fontWeight: 500,
        }}>
          <div
            style={{
              width: '12px',
              height: '12px',
              border: '2px solid rgba(99, 102, 241, 0.3)',
              borderTopColor: '#6366f1',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }}
          />
          <span>Analysis in progress — results will update when ready</span>
        </div>
      )}

      {/* Fastest Laps Section */}
      {(fastestLap || fastestOptimalLap) && (
        <div style={{ marginBottom: '3rem' }}>
          <h3 style={{
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: '#f3f4f6',
            marginBottom: '1.5rem',
            marginLeft: '1.5rem'
          }}>
            Fastest Laps
          </h3>

          <div style={{
            display: 'grid',
            gridTemplateColumns: fastestLap && fastestOptimalLap && fastestLap.lap_number !== fastestOptimalLap.lap_number ? '1fr 1fr' : '1fr',
            gap: '1.5rem',
            maxWidth: fastestLap && fastestOptimalLap && fastestLap.lap_number !== fastestOptimalLap.lap_number ?
              '1000px' :
              '500px',
            margin: '0 auto',
            padding: '0 1.5rem'
          }}
          className="fastest-laps-grid">
            {/* Fastest Lap Card */}
            {fastestLap && (
              <div style={{
                backgroundColor: hoverStyles.default,
                borderRadius: '1rem',
                border: '1px solid var(--surface-selected)',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                padding: '1.5rem',
                cursor: hasLapGpsData(fastestLap) ? 'pointer' : 'default',
                transition: 'background-color 0.2s ease'
              }}
              onClick={() => {
                if (hasLapGpsData(fastestLap)) {
                  handleShowPathVisualization(fastestLap, true);
                }
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = hoverStyles.hover;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = hoverStyles.default;
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  <div>
                    <h4 style={{ color: '#f3f4f6', fontSize: typography.h4, fontWeight: 'bold', margin: 0 }}>Fastest Lap</h4>
                    <p style={{ color: colors.textSecondary, fontSize: '0.875rem', margin: 0, marginTop: '0.125rem', marginBottom: '0.75rem' }}>Best actual lap time</p>
                  </div>
                  <div style={{ color: '#f3f4f6', fontSize: typography.h4, fontWeight: 'bold' }}>
                    Lap {fastestLap.lap_number}
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                  <div>
                    <div style={lapTimeStyles.label}>Lap Time</div>
                    <div style={{ ...lapTimeStyles.value, color: '#f3f4f6' }}>
                      {formatTime(getSessionLapTime(fastestLap))}
                    </div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={lapTimeStyles.label}>Delta Optimal</div>
                    <div style={{ ...lapTimeStyles.value, color: colors.danger }}>
                      {formatTimeDifference(getSessionLapTime(fastestLap) - fastestLap.optimal.lap_time)}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={lapTimeStyles.label}>Optimal Time</div>
                    <div style={{ ...lapTimeStyles.value, color: '#f3f4f6' }}>
                      {formatTime(fastestLap.optimal.lap_time)}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Fastest Optimal Lap Card */}
            {fastestOptimalLap && fastestLap && fastestOptimalLap.lap_number !== fastestLap.lap_number && (
              <div style={{
                backgroundColor: hoverStyles.default,
                borderRadius: '1rem',
                border: `2px solid ${colors.primary}`,
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                padding: '2rem',
                minHeight: '200px',
                cursor: 'pointer',
                transition: hoverStyles.transition,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
              }}
              onClick={() => {
                if (hasLapGpsData(fastestOptimalLap)) {
                  handleShowPathVisualization(fastestOptimalLap, true);
                }
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = hoverStyles.hover;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = hoverStyles.default;
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  <div>
                    <h4 style={{ color: colors.textPrimary, fontSize: typography.h4, fontWeight: 'bold', margin: 0 }}>Fastest Optimal Lap</h4>
                    <p style={{ color: colors.textSecondary, fontSize: '0.875rem', margin: 0, marginTop: '0.125rem', marginBottom: '0.75rem' }}>Best optimal lap, indicating the best line</p>
                  </div>
                  <div style={{ color: colors.textPrimary, fontSize: typography.h4, fontWeight: 'bold' }}>
                    Lap {fastestOptimalLap.lap_number}
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                  <div>
                    <div style={lapTimeStyles.label}>Lap Time</div>
                    <div style={{ ...lapTimeStyles.value, color: colors.textPrimary }}>
                      {formatTime(getSessionLapTime(fastestOptimalLap))}
                    </div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={lapTimeStyles.label}>Delta Optimal</div>
                    <div style={{ ...lapTimeStyles.value, color: colors.danger }}>
                      {formatTimeDifference(getSessionLapTime(fastestOptimalLap) - fastestOptimalLap.optimal.lap_time)}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={lapTimeStyles.label}>Optimal Time</div>
                    <div style={{ ...lapTimeStyles.value, color: colors.primary }}>
                      {formatTime(fastestOptimalLap.optimal.lap_time)}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* All Laps Section */}
      {laps.length > 0 && (
        <div>
          <h3 style={{
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: '#f3f4f6',
            marginBottom: '1.5rem',
            marginLeft: '1.5rem',
            display: 'flex',
            alignItems: 'center'
          }}>
            All Laps ({laps.length})
          </h3>

          <div style={{
            backgroundColor: hoverStyles.default,
            borderRadius: '1rem',
            border: '1px solid var(--surface-selected)',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            width: 'fit-content',
            maxWidth: '100%',
            overflowX: 'auto',
            margin: '0 auto'
          }}>
            <div>
              <table className="responsive-table">
                <thead style={{
                  backgroundColor: 'var(--surface-hover)',
                  borderBottom: '3px solid #6366f1'
                }}>
                  <tr>
                    <th style={{ ...thStyle, backgroundColor: 'rgba(75, 85, 99, 0.15)' }}>Lap</th>
                    <th style={thStyle}>Lap Time</th>
                    <th style={thStyle}>Delta Best Lap</th>
                    <th
                      onClick={() => setShowOptimalTimePopup(true)}
                      style={{ ...thStyle, cursor: 'pointer' }}
                      aria-label="Learn about optimal time calculation"
                    >
                      Optimal Timeⁱ
                    </th>
                    <th style={thStyle}>Delta Optimal</th>
                    <th style={{ ...thStyle, padding: '1.5rem 0.25rem', width: '30px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {laps.map((lap: any, index: number) => {
                    const isAnalyzed = !!lap.optimal;
                    const isFastestLap = fastestLap && lap.lap_number === fastestLap.lap_number;
                    const isFastestOptimal = fastestOptimalLap && lap.lap_number === fastestOptimalLap.lap_number;
                    const lapTime = getSessionLapTime(lap);
                    const deltaOptimal = isAnalyzed ? lapTime - lap.optimal.lap_time : null;
                    const deltaBestLap = fastestLap ? lapTime - getSessionLapTime(fastestLap) : 0;
                    const hasGpsData = !isAnalyzed || hasLapGpsData(lap);

                    return (
                      <tr
                        key={lap.lap_number}
                        onClick={() => {
                          if (hasGpsData) {
                            handleShowPathVisualization(lap, isAnalyzed);
                          }
                        }}
                        style={{
                          backgroundColor: isFastestLap ? 'rgba(16, 185, 129, 0.1)' :
                                         isFastestOptimal ? 'rgba(139, 92, 246, 0.1)' : hoverStyles.default,
                          borderBottom: '1px solid #374151',
                          transition: hoverStyles.transition,
                          cursor: hasGpsData ? 'pointer' : 'default'
                        }}
                        onMouseEnter={(e) => {
                          if (hasGpsData) {
                            e.currentTarget.style.backgroundColor = isFastestLap ? 'rgba(16, 185, 129, 0.2)' :
                                                                   isFastestOptimal ? 'rgba(139, 92, 246, 0.2)' : hoverStyles.hover;
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = isFastestLap ? 'rgba(16, 185, 129, 0.1)' :
                                                                 isFastestOptimal ? 'rgba(139, 92, 246, 0.1)' : hoverStyles.default;
                        }}
                      >
                        <td style={{
                          padding: '0.625rem 1rem',
                          backgroundColor: isFastestLap ? 'rgba(16, 185, 129, 0.15)' :
                                         isFastestOptimal ? 'rgba(139, 92, 246, 0.15)' : 'var(--surface-light)',
                          textAlign: 'center'
                        }}>
                          <span style={{
                            color: '#f3f4f6',
                            fontWeight: 'bold',
                            fontSize: typography.dataLarge
                          }}>
                            {lap.lap_number}
                          </span>
                        </td>
                        <td style={{textAlign: 'center'}}>
                          <div style={{
                            fontWeight: 'bold',
                            fontSize: typography.dataLarge,
                            color: '#f3f4f6'
                          }}>
                            {formatTime(lapTime)}
                          </div>
                        </td>
                        <td style={{textAlign: 'center'}}>
                          <div style={{
                            fontWeight: 'bold',
                            fontSize: typography.dataLarge,
                            color: getDeltaBestLapColor(deltaBestLap)
                          }}>
                            {formatTimeDifference(deltaBestLap)}
                          </div>
                        </td>
                        <td style={{textAlign: 'center'}}>
                          <div style={{
                            fontWeight: 'bold',
                            fontSize: typography.dataLarge,
                            color: isAnalyzed ? '#4ade80' : '#6b7280'
                          }}>
                            {isAnalyzed ? formatTime(lap.optimal.lap_time) : '—'}
                          </div>
                        </td>
                        <td style={{textAlign: 'center'}}>
                          <div style={{
                            fontWeight: 'bold',
                            fontSize: typography.dataLarge,
                            color: deltaOptimal !== null ? getDeltaOptimalColor(deltaOptimal) : '#6b7280'
                          }}>
                            {deltaOptimal !== null ? formatTimeDifference(deltaOptimal) : '—'}
                          </div>
                        </td>
                        <td style={{padding: '1.25rem 0.25rem', textAlign: 'center', width: '30px'}}>
                          {hasGpsData ? (
                            <FiChevronRight
                              size={16}
                              style={{
                                color: '#9ca3af',
                                transition: 'color 0.2s'
                              }}
                            />
                          ) : (
                            <span style={{
                              color: '#6b7280',
                              fontSize: '10px',
                              fontStyle: 'italic'
                            }}>
                              No GPS
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <OptimalTimePopup
        isOpen={showOptimalTimePopup}
        onClose={() => setShowOptimalTimePopup(false)}
      />
    </div>
  );
};

export default AnalysisResults;
